import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { request } from "@/shared/http/client";

/**
 * Quem poderia assumir uma conversa agora — e por que os outros não.
 *
 * ── POR QUE FICA AO LADO DE `useTeam` E NÃO DENTRO DELE ───────────────────
 *
 * `useTeam` é quem pertence ao workspace: muda quando alguém aceita um convite, e é
 * cacheado por um minuto. Isto é um número que anda a cada conversa atribuída.
 * Servir os dois na mesma consulta faria a segunda resposta ficar velha ou a
 * primeira ficar cara.
 *
 * ── POR QUE O MOTIVO CHEGA PRONTO ─────────────────────────────────────────
 *
 * O `reason` vem do servidor em palavras, e é o único conteúdo que faz a coluna
 * valer: um pontinho verde responde "está conectado", que não é a pergunta que
 * alguém tem na mão. A regra que o escreve é uma função pura com teste em
 * `workspace/domain/presence.ts`, no back — a tela de plataforma desenha as MESMAS
 * linhas, e um código a ser traduzido aqui viraria a mesma frase escrita duas vezes.
 */
export interface Presence {
	readonly id: string;
	readonly name: string;
	readonly role: string;
	readonly status: string;
	readonly lastSeenAt: string | null;
	readonly declaredState: "available" | "busy" | "away";
	readonly openConversations: number;
	readonly maxConcurrent: number | null;
	readonly ready: boolean;
	readonly reason: string | null;
}

export const presenceKey = ["workspace", "presence"] as const;

export function usePresence() {
	return useQuery({
		queryKey: presenceKey,
		queryFn: () => request<{ presence: Presence[] }>("/v3/workspace/presence"),
		select: (data) => data.presence,
		staleTime: 30_000,
	});
}

/**
 * Dizer que você saiu, ou quanto aguenta de uma vez.
 *
 * Só a sua própria linha: um colega marcar você como ausente é um recado que ele
 * deveria mandar, não um estado que deveria escrever. O servidor não oferece outro
 * caminho, e é ele a autoridade.
 */
export function useDeclarePresence() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			declaredState?: Presence["declaredState"];
			maxConcurrent?: number | null;
		}) =>
			request<{ presence: Presence }>("/v3/workspace/presence", { method: "PATCH", body: input }),
		onSuccess: () => client.invalidateQueries({ queryKey: presenceKey }),
	});
}
